import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from '../../i18n';
import { WebSocketAdapter } from '../../utils/WebSocketAdapter';
import { getSoundsEnabled, setSoundsEnabled, getVolume, setVolume } from '../../utils/sounds';
import { AppIcon } from './icons';

interface RoomSettings {
  timerDuration?: number;
  category?: string;
  whosPlaying?: string;
  numberOfQuestions?: number;
}

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  socket: WebSocketAdapter;
  roomName: string;
  currentSettings?: RoomSettings;
}

const TIMER_VALUES = [15000, 20000, 30000, 45000];
const QUESTION_COUNT_VALUES = [5, 10, 15, 20, 25, 30];
const DEFAULT_NUMBER_OF_QUESTIONS = 20;

const SettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onClose, socket, roomName, currentSettings }) => {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<RoomSettings>({
    timerDuration: currentSettings?.timerDuration ?? 20000,
    numberOfQuestions: currentSettings?.numberOfQuestions ?? DEFAULT_NUMBER_OF_QUESTIONS,
  });
  const [audioOn, setAudioOn] = useState(() => getSoundsEnabled());
  const [volume, setVolumeState] = useState(() => getVolume());

  const timerOptions = useMemo(
    () => TIMER_VALUES.map((value) => ({
      value,
      label: t(`lobby.settings.timerOptions.${value / 1000}`),
    })),
    [t]
  );

  const questionOptions = useMemo(
    () => QUESTION_COUNT_VALUES.map((value) => ({
      value,
      label: t(`lobby.settings.questionOptions.${value}`),
    })),
    [t]
  );

  useEffect(() => {
    if (currentSettings) {
      setSettings({
        timerDuration: currentSettings.timerDuration ?? 20000,
        numberOfQuestions: currentSettings.numberOfQuestions ?? DEFAULT_NUMBER_OF_QUESTIONS,
      });
    }
  }, [currentSettings]);

  useEffect(() => {
    if (isOpen) {
      setAudioOn(getSoundsEnabled());
      setVolumeState(getVolume());
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSave = () => {
    socket.emit('update-room-settings', {
      roomName,
      settings: {
        timerDuration: settings.timerDuration,
        numberOfQuestions: settings.numberOfQuestions,
      },
    });
    onClose();
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'rgba(15, 23, 42, 0.95)',
          backdropFilter: 'blur(10px)',
          borderRadius: '20px',
          padding: '2rem',
          maxWidth: '500px',
          width: '90%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.5)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={{ color: '#fff', marginBottom: '1.5rem', fontSize: '1.8rem', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
          <AppIcon name="settings" /> {t('lobby.settings.title')}
        </h2>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', color: '#fff', marginBottom: '0.5rem', fontSize: '1.1rem', fontWeight: 'bold' }}>
            {t('lobby.settings.timerDuration')}
          </label>
          <select
            value={settings.timerDuration ?? 20000}
            onChange={(e) => setSettings({ ...settings, timerDuration: parseInt(e.target.value) })}
            style={{
              width: '100%',
              padding: '0.8rem',
              fontSize: '1rem',
              border: '2px solid rgba(248, 250, 252, 0.3)',
              borderRadius: '10px',
              background: 'rgba(248, 250, 252, 0.1)',
              color: '#fff',
              cursor: 'pointer',
            }}
          >
            {timerOptions.map((option) => (
              <option key={option.value} value={option.value} style={{ background: '#0F172A', color: '#fff' }}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', color: '#fff', marginBottom: '0.5rem', fontSize: '1.1rem', fontWeight: 'bold' }}>
            {t('lobby.settings.numberOfQuestions')}
          </label>
          <select
            data-testid="settings-number-of-questions"
            value={settings.numberOfQuestions ?? DEFAULT_NUMBER_OF_QUESTIONS}
            onChange={(e) => setSettings({ ...settings, numberOfQuestions: parseInt(e.target.value) })}
            style={{
              width: '100%',
              padding: '0.8rem',
              fontSize: '1rem',
              border: '2px solid rgba(248, 250, 252, 0.3)',
              borderRadius: '10px',
              background: 'rgba(248, 250, 252, 0.1)',
              color: '#fff',
              cursor: 'pointer',
            }}
          >
            {questionOptions.map((option) => (
              <option key={option.value} value={option.value} style={{ background: '#0F172A', color: '#fff' }}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: '1.5rem' }}>
          <label style={{ display: 'block', color: '#fff', marginBottom: '0.5rem', fontSize: '1.1rem', fontWeight: 'bold' }}>
            {t('lobby.settings.sound')}
          </label>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: '1rem',
              cursor: 'pointer',
            }}
          >
            <span>{t('lobby.settings.soundEffects')}</span>
            <button
              type="button"
              role="switch"
              aria-checked={audioOn}
              data-testid="settings-audio-toggle"
              onClick={() => {
                const next = !audioOn;
                setAudioOn(next);
                setSoundsEnabled(next);
              }}
              style={{
                width: '48px',
                height: '26px',
                borderRadius: '13px',
                border: '2px solid rgba(248, 250, 252, 0.3)',
                background: audioOn ? 'var(--color-primary)' : 'rgba(248, 250, 252, 0.2)',
                cursor: 'pointer',
                position: 'relative',
                transition: 'background 0.2s',
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  top: '2px',
                  left: audioOn ? '24px' : '2px',
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  background: '#fff',
                  transition: 'left 0.2s',
                }}
              />
            </button>
          </label>
          <label style={{ display: 'block', color: 'rgba(255, 255, 255, 0.9)', marginTop: '0.75rem', fontSize: '0.95rem' }}>
            {t('lobby.settings.volume')}
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.35rem' }}>
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={volume}
              onChange={(e) => {
                const v = parseFloat(e.target.value);
                if (!Number.isNaN(v)) {
                  const clamped = Math.max(0, Math.min(1, v));
                  setVolumeState(clamped);
                  setVolume(clamped);
                }
              }}
              data-testid="settings-volume-slider"
              style={{
                flex: 1,
                accentColor: 'var(--color-primary)',
                cursor: 'pointer',
              }}
            />
            <span style={{ fontSize: '0.95rem', color: 'rgba(255, 255, 255, 0.8)', minWidth: '3ch' }}>
              {Math.round(volume * 100)}%
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
          <button
            onClick={onClose}
            style={{
              flex: 1,
              padding: '0.8rem',
              fontSize: '1rem',
              border: '2px solid rgba(248, 250, 252, 0.3)',
              borderRadius: '10px',
              background: 'rgba(248, 250, 252, 0.1)',
              color: '#fff',
              cursor: 'pointer',
              fontWeight: 'bold',
            }}
          >
            {t('common.cancel')}
          </button>
          <button
            data-testid="settings-save-button"
            onClick={handleSave}
            style={{
              flex: 1,
              padding: '0.8rem',
              fontSize: '1rem',
              border: 'none',
              borderRadius: '10px',
              background: 'var(--gradient-button)',
              color: '#fff',
              cursor: 'pointer',
              fontWeight: 'bold',
            }}
          >
            {t('common.save')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;
